// collector/moneyView.js
//
// The money the collector's screens say out loud (collector-clarity P1-A): what
// a line costs him, what a shop needs, what is still to buy there, and whether
// the cash on him covers the shops he has left. PURE functions over the day
// document (backend brief 2026-09-30-collector-day-read) — no Vue, no store, no
// network. They read the REBASED day, so every figure follows his taps with no
// code of its own.
//
// All money is integer cents and NOTHING is floored: a deposit larger than the
// line's total gives a negative amount, exactly as the backend's own shop
// figure does (CollectorDayBuilder::netSpentCents), so the three figures of a
// shop always add up.
//
// Two things are deliberately NOT worked out here:
//  - what he PAID at a shop is `shop.spentCents`, the backend's figure (already
//    moved on every tap by dayModel.js). It is never re-summed from the lines;
//  - the spend rule itself (found qty ?? ordered qty × shelf price ?? list
//    price) is mirrored once, in dayModel.js `lineSpend`.
// "Needed" and "still to buy" ARE summed here, from line fields the read
// already sends, until the backend sends them as fields (FE→BE brief
// 2026-10-02-collector-shop-money-fields).
import { lineSpend } from '@/collector/dayModel.js';
import { isDoneState, SHOP_STATES } from '@/collector/dayView.js';
import { isLiveLine } from '@/collector/lineView.js';

/** Outcomes of a line he may still buy: not marked yet, or parked on a member question. */
const OPEN_OUTCOMES = new Set(['pending', 'in_progress', 'issue']);

/** Shop states that still have something to buy (done and closed shops have none). */
const OPEN_SHOP_STATES = new Set([SHOP_STATES.IN_PROGRESS, SHOP_STATES.NOT_STARTED]);

function cents(value) {
  return Number(value) || 0;
}

function sum(items, pick) {
  return items.reduce((total, item) => total + pick(item), 0);
}

// ── One line ──────────────────────────────────────────────────────────────────

/**
 * What he pays at the shop if he finds the line whole at the list price: the
 * line's total less the deposit she already paid there. Not floored.
 */
export function linePayableCents(line) {
  return cents(line?.totalCents) - cents(line?.depositCents);
}

/** What his mark says he paid on the line: 0 until it is collected. Not floored. */
export function linePaidCents(line) {
  const spend = lineSpend(line);
  return spend.gross - spend.deposit;
}

/**
 * Whether the phone knows what cash he holds: only when the leader recorded an
 * allocation for him. With none the read sends `allocatedCents: 0` and
 * `withMeCents` is only minus what he has spent, so "the cash is short" would
 * be a false alarm on every screen from the first minute.
 */
export function cashKnown(day) {
  return cents(day?.cash?.allocatedCents) > 0;
}

/** A shop he is working in or has not started: the only ones that still need cash from him. */
export function isOpenShop(shop) {
  return OPEN_SHOP_STATES.has(shop?.state);
}

/** A live line he may still buy — it counts as "still to buy". */
export function isOpenLine(line) {
  return isLiveLine(line) && OPEN_OUTCOMES.has(line?.outcome);
}

// ── One shop ──────────────────────────────────────────────────────────────────

/**
 * The shop's three figures and the bar that draws them:
 *
 *   plannedCents   what the shop needs if every live line is bought whole at the list price
 *   paidCents      `shop.spentCents` — the backend's figure, never a sum over lines
 *   remainingCents the lines still to buy
 *   gapCents       planned − paid − remaining, so the three ALWAYS add up to planned:
 *                  > 0 not bought, or bought less or cheaper; < 0 he paid above the list
 *
 * `segments` are bar widths in percent of planned — paid, the gap when it is
 * positive, and the rest — all 0 when nothing is planned.
 *
 * @param {object|null} shop - a `shops[]` entry of the day
 * @returns {{ plannedCents: number, paidCents: number, remainingCents: number, gapCents: number, segments: { paid: number, gap: number, rest: number } }}
 */
export function shopMoney(shop) {
  const live = (shop?.lines ?? []).filter(isLiveLine);
  const plannedCents = sum(live, linePayableCents);
  const paidCents = cents(shop?.spentCents);
  const remainingCents = sum(live.filter(isOpenLine), linePayableCents);
  const gapCents = plannedCents - paidCents - remainingCents;
  return { plannedCents, paidCents, remainingCents, gapCents, segments: moneySegments(plannedCents, paidCents, gapCents) };
}

function moneySegments(plannedCents, paidCents, gapCents) {
  if (plannedCents <= 0) return { paid: 0, gap: 0, rest: 0 };
  const paid = Math.min(100, Math.max(0, (paidCents / plannedCents) * 100));
  const gap = Math.min(100 - paid, Math.max(0, (gapCents / plannedCents) * 100));
  return { paid, gap, rest: Math.max(0, 100 - paid - gap) };
}

/**
 * The collected lines behind «دفعت حسب علاماتك», in the shop's own line order,
 * each with the sum his mark stands for. `changed`: he found a different
 * quantity, or the shelf price differs from the list. The list explains
 * `shop.spentCents`; it never replaces it.
 *
 * @param {object|null} shop
 * @returns {Array<{ line: object, qty: number, foundQty: number, unitCents: number, priceCents: number, grossCents: number, depositCents: number, paidCents: number, changed: boolean }>}
 */
export function paidLines(shop) {
  return (shop?.lines ?? [])
    .filter((line) => isLiveLine(line) && line.outcome === 'collected')
    .map((line) => {
      const spend = lineSpend(line);
      const qty = cents(line.qty);
      const unitCents = cents(line.unitPriceCents);
      return {
        line,
        qty,
        foundQty: spend.qty,
        unitCents,
        priceCents: spend.price,
        grossCents: spend.gross,
        depositCents: spend.deposit,
        paidCents: spend.gross - spend.deposit,
        changed: spend.qty !== qty || spend.price !== unitCents,
      };
    });
}

// ── The day ───────────────────────────────────────────────────────────────────

/**
 * Whether the cash on him covers what is left: the still-to-buy of every shop
 * he is working in or has not started (a done shop, and one he left for the
 * leader, need nothing more from him) against `cash.withMeCents`. Advisory —
 * and only said when the cash is known (`cashKnown`): with no allocation
 * recorded `shortCents` is 0 and the screens say neither "covers" nor "short".
 *
 * @param {object|null} day
 * @returns {{ remainingCents: number, withMeCents: number, shortCents: number, cashKnown: boolean }}
 */
export function dayMoney(day) {
  const shops = (day?.shops ?? []).filter((shop) => isOpenShop(shop) && !shop.paidByWallet);
  const remainingCents = sum(shops, (shop) => shopMoney(shop).remainingCents);
  const withMeCents = cents(day?.cash?.withMeCents);
  const known = cashKnown(day);
  return { remainingCents, withMeCents, shortCents: known ? Math.max(0, remainingCents - withMeCents) : 0, cashKnown: known };
}

/**
 * The one amount a shop row on the day page carries, with the key of its
 * one-word label: done → what his marks paid (`shop.spentCents`), in progress →
 * what is left to buy there, not started → what the shop needs. A shop he
 * closed for the leader carries none.
 *
 * @param {object|null} shop
 * @returns {{ labelKey: string, cents: number } | null}
 */
export function shopRowMoney(shop) {
  // The club pays this shop through its wallet: no cash for him to count.
  if (shop?.paidByWallet) return null;
  if (isDoneState(shop?.state)) {
    return { labelKey: 'collector.shopRow.money.paid', cents: cents(shop?.spentCents) };
  }
  if (shop?.state === SHOP_STATES.IN_PROGRESS) {
    return { labelKey: 'collector.shopRow.money.remaining', cents: shopMoney(shop).remainingCents };
  }
  if (shop?.state === SHOP_STATES.NOT_STARTED) {
    return { labelKey: 'collector.shopRow.money.needed', cents: shopMoney(shop).remainingCents };
  }
  return null;
}

// ── One grouped card ──────────────────────────────────────────────────────────

/**
 * The figures of a grouped card, over its live lines: the pieces, the unit
 * price, the amount he pays and the deposits inside it. Lines of one product
 * can carry different prices (the group key is the product, not the variant);
 * `unitCents` is then null and the card prints each row's own price.
 *
 * @param {Array<object>} lines
 * @returns {{ qty: number, unitCents: number|null, payableCents: number, depositCents: number }}
 */
export function groupMoney(lines) {
  const live = (lines ?? []).filter(isLiveLine);
  const prices = new Set(live.map((line) => cents(line.unitPriceCents)));
  return {
    qty: sum(live, (line) => cents(line.qty)),
    unitCents: prices.size === 1 ? [...prices][0] : null,
    payableCents: sum(live, linePayableCents),
    depositCents: sum(live, (line) => cents(line.depositCents)),
  };
}
